import * as React from "react";
import { useSearchParams } from "react-router";

import { useMe } from "@/api/me";
import { useAuthStore } from "@/store/authStore";

/**
 * The page-level Details switch (MV2-838, ds/Layers "The page-level switch").
 *
 * Every surface has two layers: glance (a dot, one number, a direction, an
 * age) and detail (reasons with thresholds, provenance, ids) behind a
 * `<Fold>`. Engineers who want everything at once flip one control in the
 * page header; it opens every fold on the page.
 *
 *   - State lives in the URL as `?detail=1`, so a link carries it.
 *   - localStorage remembers the last choice as a convenience only: when the
 *     URL says nothing, the stored preference applies; the URL always wins.
 *   - Site Owners never see the switch — their surfaces stop at the glance
 *     layer and are written in owner voice. For them `on` is always false.
 *
 * `<DetailsProvider>` mounts once inside the router (the authenticated shell);
 * `useDetails()` reads it anywhere below; `<Fold>` obeys it automatically.
 */

const PARAM = "detail";
const STORAGE_KEY = "mv2.details";

export interface DetailsState {
  /** Every fold on the page is open. Always false for a Site Owner. */
  on: boolean;
  /** Whether this persona gets the switch at all. */
  available: boolean;
  setOn: (next: boolean) => void;
}

const DetailsContext = React.createContext<DetailsState | null>(null);

function readStored(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function writeStored(on: boolean): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, on ? "1" : "0");
  } catch {
    /* private mode, quota — a convenience only, never an error */
  }
}

export function DetailsProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const [params, setParams] = useSearchParams();
  const status = useAuthStore((s) => s.status);
  const { data: me } = useMe({ enabled: status === "authenticated" });
  const available = me?.primary_persona !== "SiteOwner";

  const param = params.get(PARAM);
  // URL first; the stored preference only fills a silence.
  const [stored, setStored] = React.useState(readStored);
  const on = available && (param !== null ? param === "1" : stored);

  const setOn = React.useCallback(
    (next: boolean) => {
      writeStored(next);
      setStored(next);
      setParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (next) p.set(PARAM, "1");
          else p.delete(PARAM);
          return p;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const value = React.useMemo<DetailsState>(
    () => ({ on, available, setOn }),
    [on, available, setOn],
  );
  return (
    <DetailsContext.Provider value={value}>{children}</DetailsContext.Provider>
  );
}

/**
 * Read the page-level Details state. Outside a provider (a public page, a
 * unit test without one) it is simply off and unavailable.
 */
export function useDetails(): DetailsState {
  const ctx = React.useContext(DetailsContext);
  return ctx ?? OFF;
}

const OFF: DetailsState = { on: false, available: false, setOn: () => {} };
